/**
 * s-log (instr/cl/search_unify1 C · search_unify1_design §2·§3·§4) — 사람 확정 검색 기록 · 학습 1~5 의 «순수» 부분.
 * DB·네트워크 없음(테스트 대상). 서버 부분은 log-server.ts, 클라이언트 비콘은 log-client.ts.
 *
 * 규약(1 검색 = 1 행):
 *   · 확정 = Enter(→ /search 결과 페이지 진입) · 제안 선택(→ 현장) 두 가지뿐. 키 입력·입력 멈춤은 기록하지 않는다.
 *   · 결과 클릭은 «같은 행» update(clicked_rank · selected_slug).
 *   · 저장 전 숫자 8자리 이상(전화번호 등 · 구분자 공백·점·하이픈 허용)은 '***' 로 가린다 — DB 트리거 search_mask_digits 와 같은 규칙.
 *   · 사람 = bot_type 'human'(bot-classify S10-1). source 없는 행(옛 /api/search 키 입력 기록)은 확정 검색이 아니다.
 *
 * 학습(LLM 0 · 매일 · observe 크론 09시 KST 틱):
 *   1 동의어   같은 세션 「A(0건·무클릭) → 30분 안 B → 현장 S 클릭」이 서로 다른 세션 2회 이상 → A→S(search_learned_alias)
 *   2 클릭률   (query_norm, slug) 30일 — 뷰 search_click_stats_30d(가중 최대 0.3 · 클릭 3 이상). 여기에는 같은 식의 JS 판(clickBoost)만.
 *   3 공백     0건 질의가 서로 다른 세션 3회 이상 · 30일 안 결과 있는 검색 0 · 학습 1 로 안 풀림 → keyword_rank_targets(tracked=false · note='search_gap')
 *   4 인기 비중 min(0.4, 지역 30일 사람 확정 검색 / 500) — SQL search_region_share(s-pop 이 부른다) · JS 판 regionShare
 *   5 지역 추정 지역 미선택 검색은 클릭한 현장의 지역으로 region_code 보정(region_inferred=true)
 * 필터(학습·공백·보고에 쓰는 질의): 1글자 · 자모만 · 숫자만 · 가린 번호 · 비속어·광고·URL·연락처(board/spam scanSpam) 제외.
 */
import { KST_OFFSET_MS, kstYmd } from '@/lib/cl-core/kst';
import { CANONICAL_ORIGIN } from '@/lib/cl-core/config';
import { scanSpam } from '@/lib/board/spam';

/** 로그 source — 검색창 위치. search_page 는 옛 값(= search). */
export const SEARCH_LOG_SOURCES = ['header', 'hero', 'search', 'hub'] as const;
export type SearchLogSource = (typeof SEARCH_LOG_SOURCES)[number];
/** v2-log 가 받는 값(옛 값 포함 — 게시판 글쓰기 현장 고르기 등). */
export const SEARCH_LOG_SOURCES_ACCEPTED: ReadonlySet<string> = new Set([
  ...SEARCH_LOG_SOURCES, 'search_page', 'board_write', 'modal', 'region',
]);

export function normalizeLogSource(raw: unknown, fallback: string = 'search'): string {
  const v = typeof raw === 'string' ? raw.trim() : '';
  return SEARCH_LOG_SOURCES_ACCEPTED.has(v) ? v : fallback;
}
/** 집계용 — 옛 search_page 는 search 로 접는다. */
export function sourceBucket(raw: string | null | undefined): string {
  if (!raw) return 'unknown';
  return raw === 'search_page' ? 'search' : raw;
}

/* ─── 마스킹 · 정규화 ───────────────────────────────────────────────────────── */

/** 숫자 8자리 이상(사이 공백·점·하이픈 1칸 허용) → '***'. SQL search_mask_digits 와 같은 패턴. */
const DIGIT_RUN_RE = /[0-9](?:[ .-]?[0-9]){7,}/g;
export const MASK_TOKEN = '***';

export function maskDigitRuns(q: string | null | undefined): string {
  return String(q ?? '').replace(DIGIT_RUN_RE, MASK_TOKEN);
}

/** 학습 키 — 가림 → NFC → 공백 전부 제거 → 소문자. SQL search_query_norm 과 같은 식(s-match 가 같은 키로 찾는다).
 *  L6 tabs-r1 점검 — search_logs.query_norm 이 null 인 행은 전부 트리거(sql/cl/search/s-log/01 · trg_search_logs_prepare) 적용 «전» 행이고
 *    사람(bot_type human) 행은 05_backfill ② 로 0건. 적용 뒤 신규 행은 100% 채워진다(쓰기 경로 셋 — log_search_v2 · 직접 insert · 확정 update — 모두 트리거를 탄다).
 *    JS 쪽은 이 함수가 유일한 정규화(null·비문자 입력도 빈 문자열로 받아 예외 0) · 읽기는 queryNormOf 가 옛 행(null)을 같은 식으로 메운다. */
export function searchQueryNorm(q: unknown): string {
  const raw = typeof q === 'string' ? q : q == null ? '' : String(q);
  return maskDigitRuns(raw).normalize('NFC').replace(/\s+/g, '').toLowerCase();
}

const JAMO_ONLY_RE = /^[ㄱ-ㆎᄀ-ᇿ]+$/;
const DIGITS_ONLY_RE = /^[0-9.\-]+$/;

/** 학습·공백 후보·보고에 써도 되는 질의인가. */
export function isLearnableQuery(q: string | null | undefined): boolean {
  const raw = String(q ?? '');
  const norm = searchQueryNorm(raw);
  if ([...norm].length < 2) return false; // 1글자
  if (norm.includes(MASK_TOKEN) || norm.includes('*')) return false; // 가린 번호 = 개인 식별 가능
  if (JAMO_ONLY_RE.test(norm)) return false; // 자모만(ㅅㄱㅅ · ㅇㅇ)
  if (DIGITS_ONLY_RE.test(norm)) return false; // 숫자만
  // 비속어 · 도박·성인 광고 · 리딩방 · 매물 광고 · 메신저 ID · 이메일 · 전화 · URL — 게시판 판정기 그대로(링크 0 허용).
  if (scanSpam(raw, { allowLinks: 0 })) return false;
  return true;
}

/** selected_slug 가 현장(apt_sites.slug)인가 — 글·지역·종목 클릭은 경로('/…')로 남긴다. */
export function isSiteSlug(slug: string | null | undefined): slug is string {
  return typeof slug === 'string' && slug.length > 0 && !slug.startsWith('/');
}

/** 결과 링크 href → selected_slug. /apt/{slug} 면 slug(디코드), 그 밖엔 경로(쿼리 제외 · 200자). */
export function slugFromHref(href: string | null | undefined): string | null {
  const raw = String(href ?? '').trim();
  if (!raw) return null;
  let path = raw;
  try { path = new URL(raw, CANONICAL_ORIGIN).pathname; } catch { /* 상대 경로 그대로 */ }
  const m = /^\/apt\/([^/?#]+)\/?$/.exec(path);
  if (m) {
    let s = m[1];
    try { s = decodeURIComponent(s); } catch { /* 원문 */ }
    return s.slice(0, 200);
  }
  return path.slice(0, 200);
}

/* ─── 행 ──────────────────────────────────────────────────────────────────── */

export interface SearchLogRow {
  id: string;
  query: string | null;
  query_norm?: string | null;
  results_count: number | null;
  clicked_rank: number | null;
  selected_slug: string | null;
  session_id: string | null;
  source: string | null;
  bot_type: string | null;
  created_at: string;
  region_code?: string | null;
}

/** 행의 학습 키 — 저장된 query_norm(트리거 값) · 없으면(트리거 전 옛 행) 같은 식으로 계산. */
export const queryNormOf = (r: Pick<SearchLogRow, 'query' | 'query_norm'>): string => (r.query_norm && r.query_norm.length ? r.query_norm : searchQueryNorm(r.query));
const normOf = queryNormOf;
const tOf = (r: SearchLogRow) => Date.parse(r.created_at);

/** 사람 «확정» 검색 행인가 — bot_type human · source 있음(옛 키 입력 기록 제외). */
export function isConfirmedHuman(r: SearchLogRow): boolean {
  return r.bot_type === 'human' && !!r.source;
}

/* ─── 학습 1 — 동의어 ─────────────────────────────────────────────────────── */

export const ALIAS_WINDOW_MS = 30 * 60 * 1000;
export const ALIAS_MIN_SESSIONS = 2;

export interface LearnedAlias {
  query_norm: string;
  slug: string;
  sessions: number;
  first_seen: string;
  last_seen: string;
}

/**
 * 같은 세션에서 A(0건 · 클릭 없음) 다음 30분 안의 다른 질의 B 가 현장 S 를 클릭 → (norm(A), S) 한 세션.
 * 서로 다른 세션 minSessions 이상인 쌍만 돌려준다(sessions 내림차순).
 */
export function learnAliases(
  rows: readonly SearchLogRow[],
  opts: { windowMs?: number; minSessions?: number } = {},
): LearnedAlias[] {
  const windowMs = opts.windowMs ?? ALIAS_WINDOW_MS;
  const minSessions = opts.minSessions ?? ALIAS_MIN_SESSIONS;
  const bySession = new Map<string, SearchLogRow[]>();
  for (const r of rows) {
    if (!isConfirmedHuman(r) || !r.session_id || !Number.isFinite(tOf(r))) continue;
    const arr = bySession.get(r.session_id) ?? [];
    arr.push(r);
    bySession.set(r.session_id, arr);
  }
  const acc = new Map<string, { query_norm: string; slug: string; sessions: Set<string>; first: number; last: number }>();
  for (const [sid, list] of bySession) {
    list.sort((a, b) => tOf(a) - tOf(b));
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if ((a.results_count ?? 0) !== 0 || a.clicked_rank != null || a.selected_slug) continue;
      if (!isLearnableQuery(a.query)) continue;
      const na = normOf(a);
      const ta = tOf(a);
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        const tb = tOf(b);
        if (tb - ta > windowMs) break;
        if (tb <= ta || !isSiteSlug(b.selected_slug)) continue;
        if (normOf(b) === na) continue; // 같은 질의 다시 친 것은 동의어가 아니다
        const key = `${na}\u0000${b.selected_slug}`;
        const e = acc.get(key) ?? { query_norm: na, slug: b.selected_slug, sessions: new Set<string>(), first: ta, last: tb };
        e.sessions.add(sid);
        e.first = Math.min(e.first, ta);
        e.last = Math.max(e.last, tb);
        acc.set(key, e);
      }
    }
  }
  return [...acc.values()]
    .filter((e) => e.sessions.size >= minSessions)
    .map((e) => ({ query_norm: e.query_norm, slug: e.slug, sessions: e.sessions.size, first_seen: new Date(e.first).toISOString(), last_seen: new Date(e.last).toISOString() }))
    .sort((x, y) => y.sessions - x.sessions || x.query_norm.localeCompare(y.query_norm));
}

/* ─── 학습 2 — 클릭률 가산(뷰 search_click_stats_30d 와 같은 식) ─────────────── */

export const CLICK_BOOST_MAX = 0.3;
export const CLICK_BOOST_MIN_CLICKS = 3;
export function clickBoost(clicks: number, searches: number): number {
  if (!(clicks >= CLICK_BOOST_MIN_CLICKS) || !(searches > 0)) return 0;
  return Math.round(Math.min(CLICK_BOOST_MAX, CLICK_BOOST_MAX * (clicks / searches)) * 10000) / 10000;
}

/* ─── 학습 3 — 공백(등록 안 된 현장 후보) ─────────────────────────────────── */

export const GAP_MIN_SESSIONS = 3;
export interface GapCandidate { query_norm: string; keyword: string; sessions: number; searches: number }

export function gapCandidates(
  rows: readonly SearchLogRow[],
  resolvedNorms: ReadonlySet<string>,
  opts: { minSessions?: number } = {},
): GapCandidate[] {
  const minSessions = opts.minSessions ?? GAP_MIN_SESSIONS;
  const hit = new Set<string>();
  const acc = new Map<string, { sessions: Set<string>; searches: number; forms: Map<string, number> }>();
  for (const r of rows) {
    if (!isConfirmedHuman(r)) continue;
    const n = normOf(r);
    if ((r.results_count ?? 0) > 0) { hit.add(n); continue; }
    if (!r.session_id || !isLearnableQuery(r.query)) continue;
    const e = acc.get(n) ?? { sessions: new Set<string>(), searches: 0, forms: new Map<string, number>() };
    e.sessions.add(r.session_id);
    e.searches++;
    const form = maskDigitRuns(r.query).replace(/\s+/g, ' ').trim();
    e.forms.set(form, (e.forms.get(form) ?? 0) + 1);
    acc.set(n, e);
  }
  const out: GapCandidate[] = [];
  for (const [n, e] of acc) {
    if (e.sessions.size < minSessions || hit.has(n) || resolvedNorms.has(n)) continue;
    const keyword = [...e.forms.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
    out.push({ query_norm: n, keyword, sessions: e.sessions.size, searches: e.searches });
  }
  return out.sort((a, b) => b.sessions - a.sessions || b.searches - a.searches || a.query_norm.localeCompare(b.query_norm));
}

/* ─── 학습 4 — 인기 비중 ──────────────────────────────────────────────────── */

export const REGION_SHARE_MAX = 0.4;
export const REGION_SHARE_FULL = 500;
export function regionShare(humanSearches30d: number): number {
  const n = Math.max(0, Number(humanSearches30d) || 0);
  return Math.round(Math.min(REGION_SHARE_MAX, n / REGION_SHARE_FULL) * 10000) / 10000;
}

/* ─── 학습 5 — 지역 추정 ──────────────────────────────────────────────────── */

/** 지역 미선택 · 현장 클릭 행 → { id, region_code }. slugRegion = slug → 시군구 코드(없으면 시도). */
export function inferRegions(rows: readonly SearchLogRow[], slugRegion: ReadonlyMap<string, string>): { id: string; region_code: string }[] {
  const out: { id: string; region_code: string }[] = [];
  for (const r of rows) {
    if (!isConfirmedHuman(r) || r.region_code || !isSiteSlug(r.selected_slug)) continue;
    const code = slugRegion.get(r.selected_slug);
    if (code) out.push({ id: r.id, region_code: code });
  }
  return out;
}

/* ─── 집계(어드민 「검색」 탭 · 주간 보고) ─────────────────────────────────── */

const KST = KST_OFFSET_MS; // 정본 cl-core/kst
export const kstDay = (iso: string | number | Date): string => kstYmd(new Date(iso)); // 정본 cl-core/kst(동일성 잠금: __tests__/seo/kst-consolidation-d1)

export interface SearchDaily { date: string; searches: number; sessions: number; zero: number; clicked: number }
export interface SearchStats {
  from: string;
  to: string;
  searches: number;
  sessions: number;
  zeroRate: number | null;
  firstClickRate: number | null;
  clickRate: number | null;
  avgClickRank: number | null;
  daily: SearchDaily[];
  topZero: { query: string; query_norm: string; sessions: number; searches: number }[];
  bySource: { source: string; n: number; share: number }[];
}

const rate = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 10000) / 10000 : null);

/** 사람 확정 검색만 센다. 0건 검색어 TOP 은 학습 필터를 통과한 질의만(보고·화면에 개인 식별 문자열 0). */
export function aggregateSearchStats(rows: readonly SearchLogRow[], opts: { now?: number; days?: number; topN?: number } = {}): SearchStats {
  const now = opts.now ?? Date.now();
  const days = opts.days ?? 30;
  const topN = opts.topN ?? 20;
  const fromT = now - days * 86400000;
  const hs = rows.filter((r) => isConfirmedHuman(r) && tOf(r) >= fromT && tOf(r) <= now);
  const daily = new Map<string, { searches: number; sessions: Set<string>; zero: number; clicked: number }>();
  for (let t = fromT; t <= now; t += 86400000) daily.set(kstDay(t), { searches: 0, sessions: new Set(), zero: 0, clicked: 0 });
  daily.set(kstDay(now), daily.get(kstDay(now)) ?? { searches: 0, sessions: new Set(), zero: 0, clicked: 0 });
  const sessions = new Set<string>();
  const zero = new Map<string, { sessions: Set<string>; searches: number; forms: Map<string, number> }>();
  const src = new Map<string, number>();
  let z = 0, first = 0, clicked = 0, rankSum = 0;
  for (const r of hs) {
    const d = daily.get(kstDay(r.created_at));
    if (d) {
      d.searches++;
      if (r.session_id) d.sessions.add(r.session_id);
      if ((r.results_count ?? 0) === 0) d.zero++;
      if (r.clicked_rank != null) d.clicked++;
    }
    if (r.session_id) sessions.add(r.session_id);
    if ((r.results_count ?? 0) === 0) {
      z++;
      if (isLearnableQuery(r.query)) {
        const n = normOf(r);
        const e = zero.get(n) ?? { sessions: new Set<string>(), searches: 0, forms: new Map<string, number>() };
        e.sessions.add(r.session_id ?? `row:${r.id}`);
        e.searches++;
        const form = maskDigitRuns(r.query).replace(/\s+/g, ' ').trim();
        e.forms.set(form, (e.forms.get(form) ?? 0) + 1);
        zero.set(n, e);
      }
    }
    if (r.clicked_rank != null && r.clicked_rank > 0) { clicked++; rankSum += r.clicked_rank; if (r.clicked_rank === 1) first++; }
    const b = sourceBucket(r.source);
    src.set(b, (src.get(b) ?? 0) + 1);
  }
  return {
    from: kstDay(fromT),
    to: kstDay(now),
    searches: hs.length,
    sessions: sessions.size,
    zeroRate: rate(z, hs.length),
    firstClickRate: rate(first, hs.length),
    clickRate: rate(clicked, hs.length),
    avgClickRank: clicked ? Math.round((rankSum / clicked) * 100) / 100 : null,
    daily: [...daily.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, d]) => ({ date, searches: d.searches, sessions: d.sessions.size, zero: d.zero, clicked: d.clicked })),
    topZero: [...zero.entries()]
      .map(([n, e]) => ({ query: [...e.forms.entries()].sort((a, b) => b[1] - a[1])[0][0], query_norm: n, sessions: e.sessions.size, searches: e.searches }))
      .sort((a, b) => b.sessions - a.sessions || b.searches - a.searches || a.query_norm.localeCompare(b.query_norm))
      .slice(0, topN),
    bySource: [...src.entries()].sort((a, b) => b[1] - a[1]).map(([source, n]) => ({ source, n, share: rate(n, hs.length) ?? 0 })),
  };
}

/* ─── 주간 보고 본문(report/cl/search/weekly) ─────────────────────────────── */

export const SEARCH_WEEKLY_RAIL_KEY = 'report/cl/search/weekly';

/** 레일 본문 위생 — 사람이 친 질의가 실려도 MCP 확인창을 띄우는 대문자 SQL 동사가 남지 않게 소문자로(docs/RULES.md #167). */
export function railSafe(text: string): string {
  return text.replace(/(drop|delete|truncate)/gi, (m) => m.toLowerCase());
}

const pct = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

export function buildSearchWeeklyBody(input: {
  nowIso: string;
  week: SearchStats;
  prev: SearchStats;
  newAliases: LearnedAlias[];
  gaps: { keyword: string; sessions?: number | null; volume_total?: number | null }[];
  learn?: Record<string, unknown> | null;
}): string {
  const { week, prev } = input;
  const lines: string[] = [];
  lines.push(`# report/cl/search/weekly — 카더라 사이트 검색 주간(${week.from} ~ ${week.to}, KST)`);
  lines.push(`작성 ${input.nowIso} · observe 크론 월 09시 KST 틱(s-log) · 사람 확정 검색만(bot_type=human · source 있음) · LLM 0`);
  lines.push('');
  lines.push('## 요약');
  lines.push(`- 사람 확정 검색 ${week.searches}건 · 세션 ${week.sessions} (전주 ${prev.searches}건 · 세션 ${prev.sessions})`);
  lines.push(`- 0건률 ${pct(week.zeroRate)} (전주 ${pct(prev.zeroRate)}) · 첫 결과 클릭률 ${pct(week.firstClickRate)} · 평균 클릭 순위 ${week.avgClickRank ?? '—'}`);
  lines.push(`- 위치별: ${week.bySource.map((s) => `${s.source} ${s.n}(${pct(s.share)})`).join(' · ') || '—'}`);
  lines.push('');
  lines.push('## 0건률 추이(일)');
  lines.push(week.daily.map((d) => `${d.date.slice(5)} ${d.searches ? pct(d.zero / d.searches) : '—'}(${d.searches})`).join(' · '));
  lines.push('');
  lines.push(`## 신규 학습 동의어(이번 주 첫 확인 ${input.newAliases.length})`);
  if (!input.newAliases.length) lines.push('- 없음');
  for (const a of input.newAliases.slice(0, 20)) lines.push(`- ${a.query_norm} → ${a.slug} (세션 ${a.sessions})`);
  lines.push('');
  lines.push(`## 공백 후보 상위(keyword_rank_targets note=search_gap)`);
  if (!input.gaps.length) lines.push('- 없음');
  for (const g of input.gaps.slice(0, 15)) lines.push(`- ${g.keyword}${g.sessions != null ? ` · 세션 ${g.sessions}` : ''} · 네이버 월간 ${g.volume_total == null ? '미측정' : g.volume_total}`);
  lines.push('');
  lines.push('## 0건 검색어 TOP(세션 수)');
  if (!week.topZero.length) lines.push('- 없음');
  for (const t of week.topZero.slice(0, 15)) lines.push(`- ${t.query} · 세션 ${t.sessions} · ${t.searches}회`);
  if (input.learn) {
    lines.push('');
    lines.push('## 학습 실행(같은 틱)');
    lines.push('```json');
    lines.push(JSON.stringify(input.learn));
    lines.push('```');
  }
  return railSafe(lines.join('\n'));
}

/** observe 매시 틱 중 학습·보고를 도는 시각인가 — KST 09시(UTC 00시). 보고는 월요일만. */
export function searchTickPlan(now: number, force = false): { learn: boolean; weekly: boolean } {
  const k = new Date(now + KST);
  const hour = k.getUTCHours();
  const monday = k.getUTCDay() === 1;
  if (force) return { learn: true, weekly: monday };
  return { learn: hour === 9, weekly: hour === 9 && monday };
}
